/* ===========================================================================
   Bulk Size Grid — Threada-style product panel (Enterprise theme)
   - Pricing tabs (Garment/Embroidery/Print) from JSON
   - Collapsible colour panel + image swatch carousel
   - Colour click -> dispatch theme on:variant:change (main gallery switches)
   - Per-size steppers, live total, multi-variant AJAX add to cart via the
     theme's own cart drawer.
   Namespaced + guarded so it never breaks the theme.
   =========================================================================== */
(() => {
  if (window.__bsgInit) return;
  window.__bsgInit = true;

  // Built-in fallback so Embroidery/Print pricing tabs always render, even when
  // the theme's decoration_json setting is left blank.
  const BSG_DEFAULT_DECORATION = {
    tiers: ['1-5', '6-11', '12-24', '25-49', '50-74', '75-99', '100-249', '250+'],
    columns: [
      { key: 'emb_small',   group: 'emb',   label: 'Small Embroidery',           prices: [6.99, 5.50, 4.95, 4.50, 4.25, 3.99, 3.49, 3.25] },
      { key: 'emb_text',    group: 'emb',   label: 'Embroidered Text',           prices: [5.99, 4.50, 3.95, 3.50, 3.25, 2.99, 2.49, 2.25] },
      { key: 'emb_bf',      group: 'emb',   label: 'Large Front/Back Embroidery', prices: [9.95, 9.49, 8.99, 8.49, 7.99, 7.49, 7.25, 6.95] },
      { key: 'print_small', group: 'print', label: 'Small Print',                prices: [4.50, 4.25, 3.95, 3.45, 2.95, 2.50, 2.25, 1.95] },
      { key: 'print_big',   group: 'print', label: 'Large Print',                prices: [5.50, 5.25, 4.95, 4.45, 3.95, 3.50, 3.25, 2.95] }
    ],
    setup: { label: 'Embroidery Setup Per Position', note: '', price: 9.95, free_from_qty: 25, charged: 'per_position' }
  };

  class BulkSizeGrid {
    constructor(root) {
      this.root = root;
      this.colorIndex = parseInt(root.dataset.colorIndex, 10);
      this.sizeIndex = parseInt(root.dataset.sizeIndex, 10);

      this.variants = this.readJSON('[data-bsg-variants]', []);
      this.pricing = this.readJSON('[data-bsg-pricing-data]', null);
      // Read pricing meta here (not only in initPricing) so the live per-row
      // price calc + formatMoney work even when the pricing table is hidden.
      this.pricingMeta = this.readJSON('[data-bsg-pricing-meta]', {});
      this.basePrice = (this.pricingMeta && this.pricingMeta.base_price) || 0;
      this.moneyFormat = (this.pricingMeta && this.pricingMeta.money_format) || '£{{amount}}';

      this.grid = root.querySelector('[data-bsg-grid]');
      this.addBtn = root.querySelector('[data-bsg-add]');
      this.hint = root.querySelector('[data-bsg-hint]');
      this.errorBox = root.querySelector('[data-bsg-error]');
      this.totalQtyEl = root.querySelector('[data-bsg-total-qty]');
      this.unitRow = root.querySelector('[data-bsg-unit-row]');
      this.unitPriceEl = root.querySelector('[data-bsg-unit-price]');
      this.unitLabelEl = root.querySelector('[data-bsg-unit-label]');
      this.totalRow = root.querySelector('[data-bsg-total-row]');
      this.totalPriceEl = root.querySelector('[data-bsg-total-price]');
      this.decoRow = root.querySelector('[data-bsg-deco-row]');
      this.decoPriceEl = root.querySelector('[data-bsg-deco-price]');
      this.decoLabelEl = root.querySelector('[data-bsg-deco-label]');
      this.grandRow = root.querySelector('[data-bsg-grand-row]');
      this.grandPriceEl = root.querySelector('[data-bsg-grand-price]');
      this.decoFeeVariantId = (root.getAttribute('data-decoration-fee-variant') || '').trim();
      this.productTitle = (root.getAttribute('data-product-title') || '').trim();
      this.discountNoteEl = root.querySelector('[data-bsg-discount-note]');
      this.selectedColorEl = root.querySelector('[data-bsg-selected-color]');
      this.miniSwatch = root.querySelector('[data-bsg-mini-swatch]');

      this.currentColor = this.selectedColorEl ? this.selectedColorEl.textContent.trim() : null;

      this.cartDrawer = document.querySelector('cart-drawer');
      // The section element that the media-gallery listens on for variant change
      this.productSection = root.closest('.shopify-section') || document;

      this.initPricing();
      this.initColourPanel();
      this.initSwatchCarousel();
      this.bind();
      this.syncMiniSwatch();
      this.recalc();
    }

    readJSON(sel, fallback) {
      const el = this.root.querySelector(sel);
      if (!el) return fallback;
      try { const t = el.textContent.trim(); return t ? JSON.parse(t) : fallback; }
      catch (e) { return fallback; }
    }

    /* ----------------------------- pricing tabs ----------------------------- */
    initPricing() {
      const wrap = this.root.querySelector('[data-bsg-pricing]');
      if (!wrap || !this.pricing) return;
      this.pricingMeta = this.readJSON('[data-bsg-pricing-meta]', {});
      this.basePrice = (this.pricingMeta && this.pricingMeta.base_price) || 0;
      this.moneyFormat = (this.pricingMeta && this.pricingMeta.money_format) || '£{{amount}}';
      this.pricingRows = wrap.querySelector('[data-bsg-pricing-rows]');
      // Only render from JS if server-side Liquid didn't already fill the rows
      if (this.pricingRows && this.pricingRows.children.length === 0) {
        this.renderPricing('garment');
      }
      // Decoration (Embroidery / Print) data — render those tabs live too.
      this.decoration = this.readJSON('[data-bsg-decoration-data]', null);
      this.pricingWrap = wrap;
      this.renderDecoration();
      this.activeTab = 'garment';

      wrap.querySelectorAll('[data-bsg-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          wrap.querySelectorAll('[data-bsg-tab]').forEach((t) => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          const key = tab.dataset.bsgTab;
          this.activeTab = key;
          // Show the matching view, hide the others. (Both views are server-rendered.)
          const views = wrap.querySelectorAll('[data-bsg-pricing-view]');
          if (views.length) {
            views.forEach((v) => {
              if (v.dataset.bsgPricingView === key) v.removeAttribute('hidden');
              else v.setAttribute('hidden', '');
            });
          } else {
            this.renderPricing(key);
          }
        });
      });
      const closeBtn = wrap.querySelector('[data-bsg-pricing-close]');
      const body = wrap.querySelector('[data-bsg-pricing-body]');
      if (closeBtn && body) {
        closeBtn.addEventListener('click', () => {
          if (body.hasAttribute('hidden')) body.removeAttribute('hidden'); else body.setAttribute('hidden', '');
        });
      }
    }

    formatMoney(cents) {
      // Use theme Shopify.formatMoney if present for full accuracy
      try {
        if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(Math.round(cents), this.moneyFormat);
      } catch (e) { /* fall through */ }
      const sym = (this.pricingMeta && this.pricingMeta.currency) || '£';
      return sym + (Math.round(cents) / 100).toFixed(2);
    }

    surchargeFor(key) {
      if (!this.pricing) return 0;
      if (key === 'embroidery') return this.pricing.embroidery_surcharge || 0;
      if (key === 'print') return this.pricing.print_surcharge || 0;
      return this.pricing.garment_surcharge || 0;
    }

    renderPricing(key) {
      if (!this.pricingRows || !this.pricing) return;
      this.activeTab = key;
      const tiers = this.pricing.tiers || [];
      const surcharge = this.surchargeFor(key);
      const unit = (this.basePrice || 0) + surcharge;
      this.pricingRows.innerHTML = tiers.map((t) => {
        const qty = t.max ? `${t.min}-${t.max}` : `${t.min}+`;
        const pct = t.discount || 0;
        const price = unit * (1 - pct / 100);
        const label = pct === 0 ? 'No Discount' : `${pct}% Off`;
        const cls = pct === 0 ? 'bsg-pricing__nodisc' : 'bsg-pricing__off';
        return `<tr><td>${qty}</td><td class="${cls}">${label}</td><td>${this.formatMoney(price)}</td></tr>`;
      }).join('');
    }

    /* ---------------- decoration tabs (Embroidery / Print) -----------------
       Rendered live from the decoration JSON so prices update with the product
       (a per-group surcharge from the pricing JSON, if any, is applied just
       like the garment tab's garment_surcharge). Called on init and again
       whenever the selected colour / variant changes. */
    fmtDeco(amount) {
      const n = Number(amount);
      if (isNaN(n)) return amount;
      return '£' + n.toFixed(2);
    }

    decoSurcharge(group) {
      // Optional pounds-surcharge per decoration group, set in the pricing JSON.
      if (!this.pricing) return 0;
      if (group === 'emb') return Number(this.pricing.embroidery_decoration_surcharge) || 0;
      if (group === 'print') return Number(this.pricing.print_decoration_surcharge) || 0;
      return 0;
    }

    renderDecoration() {
      const wrap = this.pricingWrap;
      // Use the saved decoration JSON if valid; otherwise fall back to a built-in
      // default so the Embroidery/Print tabs are NEVER empty (e.g. when the
      // theme setting was left blank).
      let deco = this.decoration;
      if (!deco || !deco.tiers || !deco.columns) deco = BSG_DEFAULT_DECORATION;
      this.decoration = deco;
      if (!wrap || !deco || !deco.tiers) return;

      const tierLow = (t) => parseInt(String(t).replace('+', '').split('-')[0], 10) || 0;

      const buildTable = (group, includeSetup) => {
        const cols = (deco.columns || []).filter((c) => c.group === group);
        let head = '<tr><th>Qty</th>' + cols.map((c) => `<th>${c.label}</th>`).join('');
        if (includeSetup && deco.setup) {
          head += `<th>${deco.setup.label}${deco.setup.note ? ` <span class="bsg-pricing__th-note">(${deco.setup.note})</span>` : ''}</th>`;
        }
        head += '</tr>';

        const body = deco.tiers.map((tier, ti) => {
          let row = `<tr><td>${tier}</td>`;
          row += cols.map((c) => {
            const val = (Number(c.prices[ti]) || 0) + this.decoSurcharge(group);
            return `<td>${this.fmtDeco(val)}</td>`;
          }).join('');
          if (includeSetup && deco.setup) {
            if (tierLow(tier) >= (deco.setup.free_from_qty || Infinity)) {
              row += '<td class="bsg-pricing__free">FREE</td>';
            } else {
              row += `<td>${this.fmtDeco(deco.setup.price)}</td>`;
            }
          }
          row += '</tr>';
          return row;
        }).join('');

        return `<table class="bsg-pricing__table bsg-pricing__table--deco"><thead>${head}</thead><tbody>${body}</tbody></table>`;
      };

      const embView = wrap.querySelector('[data-bsg-pricing-view="embroidery"] .bsg-pricing__scroll');
      if (embView) embView.innerHTML = buildTable('emb', true);

      const printView = wrap.querySelector('[data-bsg-pricing-view="print"] .bsg-pricing__scroll');
      if (printView) printView.innerHTML = buildTable('print', false);
    }

    /* ----------------- decoration COST (charging) -------------------------
       Works out how much the chosen logos add, based on the live decoration
       table, the selected positions, their application (Embroidery/Print),
       the logo method (Text vs logo) and the total garment quantity tier.
       Returns pounds. Setup is per embroidery position and FREE at/above the
       free_from_qty threshold. */
    decoTierIndex(totalQty) {
      // Map a quantity to the decoration tier index (8 tiers).
      // Bounds: 1-5,6-11,12-24,25-49,50-74,75-99,100-249,250+
      const bounds = [5, 11, 24, 49, 74, 99, 249];
      for (let i = 0; i < bounds.length; i++) { if (totalQty <= bounds[i]) return i; }
      return 7; // 250+
    }

    // Positions that count as "large" (front/back) decoration.
    isLargePosition(pos) {
      const large = ['big front', 'big rear', 'front', 'back'];
      return large.indexOf(String(pos || '').toLowerCase()) !== -1;
    }

    // Pick the decoration price-column key for a position.
    priceColumnFor(pos, application, method) {
      const isPrint = String(application || '').toLowerCase() === 'print';
      const large = this.isLargePosition(pos);
      if (isPrint) return large ? 'print_big' : 'print_small';
      // Embroidery
      if (String(method || '').toLowerCase().indexOf('text') !== -1) return 'emb_text';
      return large ? 'emb_bf' : 'emb_small';
    }

    columnPrice(key, tierIdx) {
      const deco = this.decoration || BSG_DEFAULT_DECORATION;
      const col = (deco.columns || []).find((c) => c.key === key);
      if (!col) return 0;
      const v = Number(col.prices[tierIdx]);
      return isNaN(v) ? 0 : v;
    }

    // Total decoration cost in POUNDS for the current selections.
    decorationCost(totalQty) {
      const deco = this.decoration || BSG_DEFAULT_DECORATION;
      const positions = (this.wizard && this.wizard.positions) ? Object.keys(this.wizard.positions) : [];
      if (!positions.length || totalQty <= 0) return { perUnit: 0, setup: 0, total: 0, lines: [] };

      const tierIdx = this.decoTierIndex(totalQty);
      const setup = deco.setup || {};
      const setupFree = totalQty >= (Number(setup.free_from_qty) || Infinity);
      const setupPrice = Number(setup.price) || 0;

      let perUnit = 0;      // pounds per garment (sums across positions)
      let setupTotal = 0;   // one-off pounds
      const lines = [];

      positions.forEach((pos) => {
        const p = this.wizard.positions[pos] || {};
        const detail = (this.wizard.details && this.wizard.details[pos]) || {};
        const application = p.application || 'Embroidery';
        const method = detail.method || '';
        const key = this.priceColumnFor(pos, application, method);
        const unit = this.columnPrice(key, tierIdx);
        perUnit += unit;
        // Setup applies to embroidery positions only — and NOT to stock/trade
        // logos or "Existing Logo" (those are already digitised, so no setup fee).
        let posSetup = 0;
        const methodLc = String(method).toLowerCase();
        const isNoSetupMethod = methodLc.indexOf('stock') !== -1
          || methodLc.indexOf('trade') !== -1
          || methodLc.indexOf('existing') !== -1;
        if (String(application).toLowerCase() !== 'print' && !setupFree && !isNoSetupMethod) {
          posSetup = setupPrice;
          setupTotal += posSetup;
        }
        lines.push({ pos, application, key, unit, setup: posSetup });
      });

      const total = perUnit * totalQty + setupTotal;
      return { perUnit, setup: setupTotal, total, lines, tierIdx, setupFree };
    }

    /* --------------------------- colour panel ------------------------------- */
    initColourPanel() {
      const toggle = this.root.querySelector('[data-bsg-colour-toggle]');
      const body = this.root.querySelector('[data-bsg-colour-body]');
      const panel = this.root.querySelector('[data-bsg-colour-panel]');
      if (!toggle || !body) return;
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.classList.toggle('is-collapsed', expanded);
      });
    }

    initSwatchCarousel() {
      this.track = this.root.querySelector('[data-bsg-swatch-track]');
      this.viewport = this.root.querySelector('[data-bsg-swatch-viewport]');
      this.dotsWrap = this.root.querySelector('[data-bsg-swatch-dots]');
      this.swatchPrev = this.root.querySelector('[data-bsg-swatch-prev]');
      this.swatchNext = this.root.querySelector('[data-bsg-swatch-next]');
      if (!this.track || !this.viewport) return;

      this.page = 0;
      this.swatchEls = Array.from(this.track.querySelectorAll('.bsg-colour__swatch'));

      if (this.swatchPrev) this.swatchPrev.addEventListener('click', (e) => { e.preventDefault(); this.goPage(this.page - 1); });
      if (this.swatchNext) this.swatchNext.addEventListener('click', (e) => { e.preventDefault(); this.goPage(this.page + 1); });

      let rt;
      window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => this.layoutCarousel(), 120); });
      // initial layout after paint
      requestAnimationFrame(() => this.layoutCarousel());
      // safety re-layout once images/fonts settle
      setTimeout(() => this.layoutCarousel(), 300);
      // recalc as each swatch image finishes loading (widths change)
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.layoutCarousel(), { once: true });
      });
    }

    colsPerView() {
      // Determine columns that fit in the viewport based on actual swatch width
      const first = this.swatchEls[0];
      if (!first) return 4;
      const vpW = this.viewport.clientWidth || this.viewport.getBoundingClientRect().width;
      const swW = first.getBoundingClientRect().width || (vpW / 4);
      const gap = 8;
      const cols = Math.max(1, Math.floor((vpW + gap) / (swW + gap)));
      return cols;
    }

    pageCount() {
      const cols = this.colsPerView();
      const perPage = cols * 2; // two rows visible
      return Math.max(1, Math.ceil(this.swatchEls.length / perPage));
    }

    layoutCarousel() {
      if (!this.track || !this.swatchEls.length) return;
      const cols = this.colsPerView();
      const gap = 8;
      const vpW = this.viewport.clientWidth || this.viewport.getBoundingClientRect().width;
      const colW = Math.floor((vpW - (cols - 1) * gap) / cols);
      this.colW = colW;
      this.gap = gap;
      this.cols = cols;

      // Does everything fit without sliding? (single page)
      const totalCols = Math.ceil(this.swatchEls.length / 2);
      const fitsOnePage = this.swatchEls.length <= cols * 2;

      if (fitsOnePage) {
        // ---- Normal grid (no slider): clean wrap, left-aligned, no forced 2-row column-flow ----
        this.track.style.display = 'grid';
        this.track.style.gridTemplateRows = '';
        this.track.style.gridAutoFlow = 'row';
        this.track.style.gridAutoColumns = '';
        // up to `cols` columns; for 1-2 swatches it just shows that many
        const useCols = Math.min(cols, this.swatchEls.length);
        this.track.style.gridTemplateColumns = 'repeat(' + Math.max(1, useCols) + ', minmax(0, ' + colW + 'px))';
        this.track.style.gap = gap + 'px';
        this.track.style.transform = 'translateX(0)';
        this.viewport.classList.add('is-static');
        if (this.swatchPrev) this.swatchPrev.style.display = 'none';
        if (this.swatchNext) this.swatchNext.style.display = 'none';
        this.renderDots(1);
        return;
      }

      // ---- Slider mode (content overflows) ----
      this.viewport.classList.remove('is-static');
      this.track.style.display = 'grid';
      this.track.style.gridTemplateColumns = '';
      this.track.style.gridTemplateRows = 'auto auto';
      this.track.style.gridAutoFlow = 'column';
      this.track.style.gridAutoColumns = colW + 'px';
      this.track.style.gap = gap + 'px';
      const pages = this.pageCount();
      if (this.page > pages - 1) this.page = pages - 1;
      if (this.page < 0) this.page = 0;
      this.renderDots(pages);
      this.applyTransform();
    }

    goPage(p) {
      const pages = this.pageCount();
      this.page = Math.min(pages - 1, Math.max(0, p));
      this.applyTransform();
      this.renderDots(pages);
    }

    applyTransform() {
      const pages = this.pageCount();
      const cols = this.cols || this.colsPerView();
      const gap = this.gap || 8;
      const colW = this.colW || 0;
      // One page = cols columns + the gaps between them
      const pageStep = cols * colW + (cols) * gap;
      // Total scrollable width of the track
      const totalCols = Math.ceil(this.swatchEls.length / 2); // 2 rows
      const trackW = totalCols * colW + (totalCols - 1) * gap;
      const vpW = this.viewport.clientWidth || this.viewport.getBoundingClientRect().width;
      const maxOffset = Math.max(0, trackW - vpW);
      let offset = pageStep * this.page;
      if (offset > maxOffset) offset = maxOffset; // stop at the end, no empty space
      if (offset < 0) offset = 0;
      this.track.style.transform = `translateX(-${offset}px)`;

      // Arrows: show only when there's somewhere to go
      if (this.swatchPrev) this.swatchPrev.style.display = (pages > 1 && this.page > 0) ? 'flex' : 'none';
      if (this.swatchNext) this.swatchNext.style.display = (pages > 1 && this.page < pages - 1) ? 'flex' : 'none';
    }

    renderDots(pages) {
      if (!this.dotsWrap) return;
      if (pages <= 1) { this.dotsWrap.style.display = 'none'; this.dotsWrap.innerHTML = ''; return; }
      this.dotsWrap.style.display = 'flex';
      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bsg-colour__dot' + (i === this.page ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to colour page ' + (i + 1));
        dot.addEventListener('click', (e) => { e.preventDefault(); this.goPage(i); });
        this.dotsWrap.appendChild(dot);
      }
    }

    /* ----------------------------- variant lookup --------------------------- */
    findVariant(sizeValue) {
      // Match on size + colour. A product may also carry an extra option such as
      // "Garments Price" (1-9 … 250+) whose tiers have DIFFERENT prices. The theme
      // does not pick that tier itself (volume discount comes from a Shopify
      // automatic discount applied to the full price). So among all variants that
      // match size+colour we deliberately choose the HIGHEST-priced one (the full
      // price / "1-9" tier) to avoid double-discounting and the wrong "250+" price.
      const matches = this.variants.filter((v) => {
        const sizeOk = v.options[this.sizeIndex] === sizeValue;
        const colorOk = this.colorIndex === -1 || v.options[this.colorIndex] === this.currentColor;
        return sizeOk && colorOk;
      });
      if (!matches.length) return undefined;
      if (matches.length === 1) return matches[0];
      // Prefer an available, full-price variant.
      const available = matches.filter((v) => v.available);
      const pool = available.length ? available : matches;
      return pool.reduce((best, v) => (v.price > best.price ? v : best), pool[0]);
    }

    firstVariantForColor(color) {
      return this.variants.find((v) => this.colorIndex === -1 || v.options[this.colorIndex] === color);
    }

    /* -------------------------------- binding ------------------------------- */
    bind() {
      this.root.querySelectorAll('[data-bsg-color]').forEach((sw) => {
        sw.addEventListener('click', () => this.selectColor(sw));
      });

      this.grid.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-bsg-minus]');
        const plus = e.target.closest('[data-bsg-plus]');
        if (!minus && !plus) return;
        e.preventDefault();
        const row = e.target.closest('[data-bsg-row]');
        if (row) this.step(row, !!plus);
      });

      this.grid.addEventListener('input', (e) => {
        const input = e.target.closest('[data-bsg-qty]');
        if (!input) return;
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < 0) val = 0;
        input.value = val;
        this.recalc();
      });

      if (this.addBtn) this.addBtn.addEventListener('click', () => this.openWizard());
      this.initWizard();
    }

    /* ============================ LOGO WIZARD ============================== */
    initWizard() {
      this.modal = this.root.querySelector('[data-bsg-modal]');
      if (!this.modal) return;
      // Move the modal to <body> so no parent stacking context (transforms,
      // sticky headers, overflow) can trap it under the theme header.
      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }
      this.wizard = { positions: {}, assign: 'each', method: null, details: {} };

      this.modal.querySelectorAll('[data-bsg-modal-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeWizard()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.modal.hidden) this.closeWizard(); });

      // generic step navigation
      this.modal.querySelectorAll('[data-bsg-goto]').forEach((b) =>
        b.addEventListener('click', () => this.gotoStep(b.dataset.bsgGoto)));

      // intro: buy plain
      const plainBtn = this.modal.querySelector('[data-bsg-add-plain]');
      if (plainBtn) plainBtn.addEventListener('click', () => { this.closeWizard(); this.submitCart([]); });

      // positions: toggle selection + reveal application
      this.modal.querySelectorAll('[data-bsg-pos]').forEach((posEl) => {
        const btn = posEl.querySelector('[data-bsg-pos-toggle]');
        const app = posEl.querySelector('[data-bsg-pos-app]');
        if (btn) btn.addEventListener('click', () => {
          const name = posEl.dataset.bsgPos;
          const selected = posEl.classList.toggle('is-selected');
          if (app) app.hidden = !selected;
          if (selected) {
            const appVal = posEl.querySelector('input[type=radio]:checked');
            this.wizard.positions[name] = { application: appVal ? appVal.value : 'Embroidery' };
          } else {
            delete this.wizard.positions[name];
          }
        });
        posEl.querySelectorAll('input[type=radio]').forEach((r) =>
          r.addEventListener('change', () => {
            const name = posEl.dataset.bsgPos;
            if (this.wizard.positions[name]) this.wizard.positions[name].application = r.value;
          }));
      });

      // assignment style
      this.modal.querySelectorAll('[data-bsg-assign]').forEach((c) =>
        c.addEventListener('click', () => {
          this.modal.querySelectorAll('[data-bsg-assign]').forEach((x) => x.classList.remove('is-active'));
          c.classList.add('is-active');
          this.wizard.assign = c.dataset.bsgAssign;
          this.updateAssignView();
        }));

      // method select -> go to details
      this.modal.querySelectorAll('[data-bsg-method]').forEach((m) =>
        m.addEventListener('click', () => {
          this.modal.querySelectorAll('[data-bsg-method]').forEach((x) => x.classList.remove('is-active'));
          m.classList.add('is-active');
          this.wizard.method = m.dataset.bsgMethod;
          this.startDetails();
        }));

      // details save / another / confirm
      const saveBtn = this.modal.querySelector('[data-bsg-detail-save]');
      if (saveBtn) saveBtn.addEventListener('click', () => this.saveDetail(false));
      const anotherBtn = this.modal.querySelector('[data-bsg-detail-another]');
      if (anotherBtn) anotherBtn.addEventListener('click', () => this.saveDetail(true));
      const confirmBtn = this.modal.querySelector('[data-bsg-confirm-add]');
      if (confirmBtn) confirmBtn.addEventListener('click', () => this.confirmWizard());

      // stock / trade logo selection
      this.modal.querySelectorAll('[data-bsg-stocklogo]').forEach((b) =>
        b.addEventListener('click', () => {
          this.modal.querySelectorAll('[data-bsg-stocklogo]').forEach((x) => {
            x.classList.remove('is-active');
            x.setAttribute('aria-checked', 'false');
          });
          b.classList.add('is-active');
          b.setAttribute('aria-checked', 'true');
          this._stockLogo = b.dataset.bsgStocklogo;
          const sel = this.modal.querySelector('[data-bsg-stocklogo-selected]');
          if (sel) { sel.textContent = `Selected: ${this._stockLogo}`; sel.hidden = false; }
        }));

      this.initDropzone();
      this.initDelivery();
    }

    initDropzone() {
      const drop = this.modal.querySelector('[data-bsg-drop]');
      if (!drop) return;
      const input = drop.querySelector('[data-bsg-detail-file]');
      const btn = drop.querySelector('[data-bsg-drop-btn]');
      const fileLabel = drop.querySelector('[data-bsg-drop-filename]');
      if (btn && input) btn.addEventListener('click', () => input.click());
      if (input) input.addEventListener('change', () => {
        const f = input.files && input.files[0];
        if (fileLabel) { fileLabel.hidden = !f; fileLabel.textContent = f ? f.name : ''; }
      });
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); }));
      drop.addEventListener('drop', (e) => {
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (f && input) {
          try { const dt = new DataTransfer(); dt.items.add(f); input.files = dt.files; } catch (err) { /* older browsers */ }
          if (fileLabel) { fileLabel.hidden = false; fileLabel.textContent = f.name; }
        }
      });
    }

    initDelivery() {
      const wrap = this.modal.querySelector('[data-bsg-delivery]');
      if (!wrap) return;
      const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric' });
      const addBusinessDays = (start, n) => {
        const d = new Date(start); let added = 0;
        while (added < n) { d.setDate(d.getDate() + 1); const day = d.getDay(); if (day !== 0 && day !== 6) added++; }
        return d;
      };
      const now = new Date();
      const setText = (sel, val) => { const el = wrap.querySelector(sel); if (el) el.textContent = val; };
      setText('[data-bsg-date-plain]', fmt(addBusinessDays(now, 2)));
      setText('[data-bsg-date-express]', fmt(addBusinessDays(now, 3)));
      setText('[data-bsg-date-standard]', fmt(addBusinessDays(now, 8)));

      // countdown to next 4pm cutoff
      const timer = wrap.querySelector('[data-bsg-timer]');
      if (!timer) return;
      const tick = () => {
        const target = new Date(); target.setHours(16, 0, 0, 0);
        if (target <= new Date()) target.setDate(target.getDate() + 1);
        let diff = Math.max(0, target - new Date());
        const dd = Math.floor(diff / 86400000); diff -= dd * 86400000;
        const hh = Math.floor(diff / 3600000); diff -= hh * 3600000;
        const mm = Math.floor(diff / 60000); diff -= mm * 60000;
        const ss = Math.floor(diff / 1000);
        const set = (a, v) => { const e = timer.querySelector(`[data-${a}]`); if (e) e.textContent = v; };
        set('d', dd); set('h', hh); set('m', mm); set('s', ss);
      };
      tick();
      this._timerInterval = setInterval(tick, 1000);
    }

    openWizard() {
      if (this.getItems().length === 0) return;
      if (!this.modal) { this.submitCart([]); return; }
      this.gotoStep('intro');
      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }

    closeWizard() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    gotoStep(name) {
      if (!this.modal) return;
      // validation: leaving positions requires at least one selected
      if (name === 'assign' && Object.keys(this.wizard.positions).length === 0) {
        this.flash('Please select at least one logo position.');
        return;
      }
      this.modal.querySelectorAll('[data-bsg-step]').forEach((s) => {
        s.hidden = s.dataset.bsgStep !== name;
      });
      if (name === 'review') this.renderReview();
      if (name === 'assign') this.updateAssignView();
    }

    updateAssignView() {
      const methodsGrid = this.modal.querySelector('[data-bsg-methods]');
      const methodLabel = this.modal.querySelector('[data-bsg-method-label]');
      const perpos = this.modal.querySelector('[data-bsg-perpos]');
      if (!perpos) return;
      const each = this.wizard.assign === 'each';

      // Keep the assignment-card highlight in sync with state (default = "each").
      this.modal.querySelectorAll('[data-bsg-assign]').forEach((c) => {
        c.classList.toggle('is-active', c.dataset.bsgAssign === this.wizard.assign);
      });

      if (each) {
        if (methodsGrid) methodsGrid.hidden = true;
        if (methodLabel) methodLabel.hidden = true;
        perpos.hidden = false;
        perpos.innerHTML = '';
        Object.keys(this.wizard.positions).forEach((pos) => {
          const app = this.wizard.positions[pos].application;
          const iconImg = this.posIconHTML(pos);
          const done = this.wizard.details[pos] && this.wizard.details[pos].method;
          const row = document.createElement('div');
          row.className = 'bsg-perpos__row' + (done ? ' is-done' : '');
          const doneBadge = done
            ? `<span class="bsg-perpos__done">✓ ${this.wizard.details[pos].method}</span>`
            : '';
          row.innerHTML =
            `<div class="bsg-perpos__info"><span class="bsg-perpos__thumb">${iconImg}</span>` +
            `<span class="bsg-perpos__meta"><strong>${pos}</strong><span>${app}</span>${doneBadge}</span></div>` +
            `<div class="bsg-perpos__methods">` +
            ['Existing Logo', 'Send Artwork Later', 'Text Logo', 'Upload Your Logo', 'Trade/Stock Logos'].map((m) =>
              `<button type="button" class="bsg-perpos__m${done && this.wizard.details[pos].method === m ? ' is-selected' : ''}" data-bsg-perpos-method="${m}" data-bsg-perpos-pos="${pos}">` +
              `<span class="bsg-perpos__m-ic">${this.methodIconHTML(m)}</span><span class="bsg-perpos__m-name">${m}</span></button>`
            ).join('') +
            `</div>`;
          perpos.appendChild(row);
        });
        perpos.querySelectorAll('[data-bsg-perpos-method]').forEach((b) =>
          b.addEventListener('click', () => {
            this.wizard.method = b.dataset.bsgPerposMethod;
            this.startDetailsForPosition(b.dataset.bsgPerposPos);
          }));

        // "Continue to review" button — only enabled once every position has a method.
        const allKeys = Object.keys(this.wizard.positions);
        const allDone = allKeys.length > 0 && allKeys.every((p) => this.wizard.details[p] && this.wizard.details[p].method);
        const cont = document.createElement('button');
        cont.type = 'button';
        cont.className = 'bsg-perpos__continue';
        cont.textContent = allDone ? 'Continue to review' : 'Set up all positions to continue';
        cont.disabled = !allDone;
        cont.addEventListener('click', () => { if (allDone) this.gotoStep('review'); });
        perpos.appendChild(cont);
      } else {
        if (methodsGrid) methodsGrid.hidden = false;
        if (methodLabel) methodLabel.hidden = false;
        perpos.hidden = true;
        perpos.innerHTML = '';
      }
    }

    posIconHTML(pos) {
      const esc = (window.CSS && CSS.escape) ? CSS.escape(pos) : pos.replace(/"/g, '\\"');
      const el = this.modal.querySelector(`[data-bsg-pos="${esc}"] .bsg-pos__img`);
      return el ? el.innerHTML : '';
    }

    methodIconHTML(method) {
      const esc = (window.CSS && CSS.escape) ? CSS.escape(method) : method.replace(/"/g, '\\"');
      const btn = this.modal.querySelector(`[data-bsg-methods] [data-bsg-method="${esc}"] .bsg-method__icon`);
      return btn ? btn.innerHTML : '';
    }

    startDetailsForPosition(pos) {
      this._perPosFlow = true;
      this.detailQueue = [pos];
      this.detailIndex = 0;
      this.showDetail();
      this.gotoStep('details');
    }

    startDetails() {
      this._perPosFlow = false;
      // build a queue of positions needing details
      if (this.wizard.assign === 'same') {
        this.detailQueue = ['__ALL__'];
      } else {
        this.detailQueue = Object.keys(this.wizard.positions);
      }
      this.detailIndex = 0;
      this.showDetail();
      this.gotoStep('details');
    }

    showDetail() {
      const key = this.detailQueue[this.detailIndex];
      const posLabel = key === '__ALL__' ? 'All positions' : key;
      const app = key === '__ALL__' ? '' : ` (${this.wizard.positions[key].application})`;
      const posEl = this.modal.querySelector('[data-bsg-detail-pos]');
      if (posEl) posEl.textContent = `Position: ${posLabel}${app}`;

      const method = (this.wizard.method || '').toLowerCase();
      this.toggle('[data-bsg-detail-existing]', method === 'existing logo');
      this.toggle('[data-bsg-detail-text]', method === 'text logo');
      this.toggle('[data-bsg-detail-upload]', method === 'upload your logo');
      this.toggle('[data-bsg-detail-trade]', method === 'trade/stock logos');
      // 'send artwork later' shows just notes

      // reset fields
      const t = this.modal.querySelector('[data-bsg-detail-text-input]'); if (t) t.value = '';
      const n = this.modal.querySelector('[data-bsg-detail-notes]'); if (n) n.value = '';
      const f = this.modal.querySelector('[data-bsg-detail-file]'); if (f) f.value = '';
      // reset stock-logo selection
      this._stockLogo = '';
      this.modal.querySelectorAll('[data-bsg-stocklogo]').forEach((x) => {
        x.classList.remove('is-active');
        x.setAttribute('aria-checked', 'false');
      });
      const stockSel = this.modal.querySelector('[data-bsg-stocklogo-selected]');
      if (stockSel) { stockSel.textContent = ''; stockSel.hidden = true; }

      // last item -> Save & Continue goes to review; otherwise stays
      const anotherBtn = this.modal.querySelector('[data-bsg-detail-another]');
      if (anotherBtn) anotherBtn.style.display = (this.wizard.assign === 'each' && this.detailIndex < this.detailQueue.length - 1) ? '' : 'none';
    }

    toggle(sel, show) {
      const el = this.modal.querySelector(sel);
      if (el) el.hidden = !show;
    }

    saveDetail(another) {
      const key = this.detailQueue[this.detailIndex];
      const t = this.modal.querySelector('[data-bsg-detail-text-input]');
      const n = this.modal.querySelector('[data-bsg-detail-notes]');
      const f = this.modal.querySelector('[data-bsg-detail-file]');
      const detail = {
        method: this.wizard.method,
        text: t ? t.value : '',
        notes: n ? n.value : '',
        stock: this._stockLogo || '',
        file: (f && f.files && f.files[0]) ? f.files[0].name : '',
        fileObj: (f && f.files && f.files[0]) ? f.files[0] : null
      };
      // keep the original input element (with its file) per position for upload
      if (!this._fileInputs) this._fileInputs = {};
      if (f && f.files && f.files[0]) {
        const targetKeys = key === '__ALL__' ? Object.keys(this.wizard.positions) : [key];
        targetKeys.forEach((pk) => {
          const clone = f.cloneNode(true);
          // cloneNode doesn't copy the FileList; set it explicitly
          try { const dt = new DataTransfer(); dt.items.add(f.files[0]); clone.files = dt.files; } catch (e) { /* noop */ }
          this._fileInputs[pk] = clone;
        });
      }
      if (key === '__ALL__') {
        Object.keys(this.wizard.positions).forEach((p) => { this.wizard.details[p] = detail; });
      } else {
        this.wizard.details[key] = detail;
      }
      // advance
      if (this._perPosFlow) {
        // Per-position flow (Customise Each Position): after saving ONE position,
        // return to the position list so the user can set up the next position.
        // gotoStep('assign') re-renders the list (with done-status) via updateAssignView.
        this.gotoStep('assign');
        return;
      }
      if (this.wizard.assign === 'each' && this.detailIndex < this.detailQueue.length - 1 && another) {
        this.detailIndex += 1;
        this.showDetail();
      } else if (this.wizard.assign === 'each' && this.detailIndex < this.detailQueue.length - 1 && !another) {
        // Save & Continue moves to next position too
        this.detailIndex += 1;
        this.showDetail();
      } else {
        this.gotoStep('review');
      }
    }

    renderReview() {
      const list = this.modal.querySelector('[data-bsg-review-list]');
      if (!list) return;

      // Compute per-position costs for the current garment quantity so each line
      // can show its own amount (unit price × qty, + setup where it applies).
      const totalQty = this.getItems().reduce((s, i) => s + i.quantity, 0);
      const deco = this.decorationCost(totalQty);
      const costByPos = {};
      (deco.lines || []).forEach((l) => { costByPos[l.pos] = l; });

      const rows = Object.keys(this.wizard.positions).map((p) => {
        const app = this.wizard.positions[p].application;
        const d = this.wizard.details[p] || {};
        const bits = [];
        if (d.method) bits.push(d.method);
        if (d.stock) bits.push(`logo: ${d.stock}`);
        if (d.text) bits.push(`“${d.text}”`);
        if (d.file) bits.push(d.file);
        if (d.notes) bits.push(`note: ${d.notes}`);

        // Per-line amount
        let amountHtml = '';
        const c = costByPos[p];
        if (c && totalQty > 0) {
          const lineUnitCents = Math.round(c.unit * 100);
          const lineSetupCents = Math.round((c.setup || 0) * 100);
          const lineTotalCents = lineUnitCents * totalQty + lineSetupCents;
          const setupBit = lineSetupCents > 0 ? ` + ${this.formatMoney(lineSetupCents)} setup` : '';
          amountHtml =
            `<span class="bsg-review__amount">${this.formatMoney(lineTotalCents)}</span>` +
            `<div class="bsg-review__amount-sub">${this.formatMoney(lineUnitCents)} × ${totalQty}${setupBit}</div>`;
        }

        return `<div class="bsg-review__row">` +
          `<div class="bsg-review__row-head"><strong>${p}</strong> <span>${app}</span>${amountHtml}</div>` +
          `<div class="bsg-review__meta">${bits.join(' · ') || '—'}</div></div>`;
      });
      list.innerHTML = rows.join('') || '<p>No positions selected.</p>';

      // Append the overall decoration total.
      if (deco.total > 0) {
        const cents = Math.round(deco.total * 100);
        const note = deco.setup > 0
          ? ` (includes ${this.formatMoney(Math.round(deco.setup * 100))} setup)`
          : (deco.setupFree ? ' (setup FREE at this quantity)' : '');
        list.innerHTML +=
          `<div class="bsg-review__row bsg-review__row--cost"><strong>Decoration total</strong>` +
          `<span>${this.formatMoney(cents)}</span>` +
          `<div class="bsg-review__meta">${this.formatMoney(Math.round(deco.perUnit * 100))} per item × ${totalQty}${note}</div></div>`;
      }
    }

    flash(msg) {
      this.showError(msg);
      setTimeout(() => this.showError(false), 3000);
    }

    buildProperties() {
      const props = {};
      const positions = Object.keys(this.wizard.positions);
      if (!positions.length) return props;
      props['_Customised'] = 'Yes';
      props['Logo positions'] = positions.join(', ');
      positions.forEach((p) => {
        const app = this.wizard.positions[p].application;
        const d = this.wizard.details[p] || {};
        const parts = [app];
        if (d.method) parts.push(d.method);
        if (d.stock) parts.push(`stock logo: ${d.stock}`);
        if (d.text) parts.push(`text: ${d.text}`);
        if (d.file) parts.push(`file: ${d.file}`);
        if (d.notes) parts.push(`notes: ${d.notes}`);
        props[`Logo – ${p}`] = parts.join(' | ');
      });
      return props;
    }

    collectFiles() {
      // Map of position -> File for any uploaded logos
      const files = {};
      Object.keys(this.wizard.positions).forEach((p) => {
        const d = this.wizard.details[p] || {};
        if (d.fileObj) files[p] = d.fileObj;
      });
      return files;
    }

    confirmWizard() {
      const props = this.buildProperties();
      const files = this.collectFiles();
      this.closeWizard();
      if (Object.keys(files).length) {
        // Real file upload requires a multipart form POST (AJAX can't send files)
        this.submitCartWithFiles(props, files);
      } else {
        this.submitCart(props);
      }
    }

    /* Multipart form submit so Shopify hosts the file and the URL lands in the
       line-item property — visible/clickable in cart, checkout and the order. */
    async submitCartWithFiles(properties, files) {
      const items = this.getItems();
      if (!items.length) return;
      const T = (typeof window !== 'undefined' && window.theme) ? window.theme : {};
      const routes = T.routes || {};
      const cartAdd = (routes.cartAdd || '/cart/add').replace(/\.js$/, '');

      // Shopify's form /cart/add accepts a single id + quantity per submit, plus
      // properties[] including file inputs. To support multiple sizes we submit
      // the first item with the files+properties, then the remaining items are
      // added via AJAX (no files) right before navigating to the cart.
      const [first, ...rest] = items;

      const form = document.createElement('form');
      form.method = 'post';
      form.action = cartAdd;
      form.enctype = 'multipart/form-data';
      form.style.display = 'none';

      const addHidden = (name, value) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value; form.appendChild(i);
      };
      addHidden('id', first.id);
      addHidden('quantity', first.quantity);
      Object.keys(properties).forEach((k) => addHidden(`properties[${k}]`, properties[k]));

      // Attach each uploaded file. We move the ORIGINAL file input (which already
      // holds the user's selected file) into the form — bulletproof across browsers.
      Object.keys(files).forEach((pos) => {
        const orig = this._fileInputs && this._fileInputs[pos];
        if (orig) {
          orig.name = `properties[Logo file – ${pos}]`;
          orig.removeAttribute('hidden');
          orig.style.display = 'none';
          form.appendChild(orig);
        } else {
          // fallback: rebuild via DataTransfer
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.name = `properties[Logo file – ${pos}]`;
          try { const dt = new DataTransfer(); dt.items.add(files[pos]); fileInput.files = dt.files; } catch (e) { /* noop */ }
          form.appendChild(fileInput);
        }
      });

      const goCart = () => { document.body.appendChild(form); form.submit(); };

      // Decoration fee (penny product) goes with the AJAX batch (no files needed).
      const feeLine = this.decorationFeeLineItem(properties);
      const ajaxItems = rest.map((r) => ({ id: r.id, quantity: r.quantity, properties }));
      if (feeLine) ajaxItems.push(feeLine);

      if (ajaxItems.length) {
        // add the remaining sizes (+ fee) via AJAX, then submit the form
        fetch((routes.cartAdd || '/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: ajaxItems })
        }).then(goCart).catch(goCart);
      } else {
        goCart();
      }
    }

    step(row, isPlus) {
      const input = row.querySelector('[data-bsg-qty]');
      if (!input || input.disabled) return;
      let val = parseInt(input.value, 10) || 0;
      val = isPlus ? val + 1 : Math.max(0, val - 1);
      input.value = val;
      this.recalc();
    }

    syncMiniSwatch() {
      if (!this.miniSwatch) return;
      const active = this.root.querySelector('.bsg-colour__swatch.is-active img, .bsg-colour__swatch.is-active .bsg-colour__solid');
      if (active && active.tagName === 'IMG') {
        this.miniSwatch.style.backgroundImage = `url(${active.currentSrc || active.src})`;
        this.miniSwatch.style.backgroundColor = '';
      } else if (this.currentColor) {
        this.miniSwatch.style.backgroundImage = '';
        this.miniSwatch.style.backgroundColor = this.currentColor.toLowerCase().replace(/[^a-z]/g, '');
      }
    }

    /* ----------------------------- colour switch ---------------------------- */
    selectColor(sw) {
      const value = sw.dataset.bsgColor;
      this.currentColor = value;

      this.root.querySelectorAll('[data-bsg-color]').forEach((b) => {
        const active = b === sw;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      if (this.selectedColorEl) this.selectedColorEl.textContent = value;
      this.syncMiniSwatch();

      // Update size rows to the matching variant for the new colour
      this.grid.querySelectorAll('[data-bsg-row]').forEach((row) => {
        const variant = this.findVariant(row.dataset.size);
        const input = row.querySelector('[data-bsg-qty]');
        const priceEl = row.querySelector('[data-bsg-price]');
        const stockEl = row.querySelector('[data-bsg-stock]');
        if (!input) return;
        if (variant) {
          input.dataset.variantId = variant.id;
          input.dataset.price = variant.price;
          if (priceEl) priceEl.textContent = variant.price_money;
          input.disabled = !variant.available;
          input.toggleAttribute('aria-disabled', !variant.available);
          row.classList.toggle('is-soldout', !variant.available);
          if (stockEl) stockEl.textContent = variant.available ? 'In-Stock' : 'Sold out';
          if (!variant.available) input.value = 0;
        } else {
          input.dataset.variantId = '';
          input.disabled = true; input.value = 0;
          row.classList.add('is-soldout');
          if (stockEl) stockEl.textContent = 'Unavailable';
        }
      });

      // Tell the theme's media gallery to switch image
      const mediaId = sw.dataset.mediaId;
      const variant = this.firstVariantForColor(value);
      if (variant) {
        const detailVariant = Object.assign({}, variant);
        if (mediaId) detailVariant.featured_media = { id: Number(mediaId) };
        else if (variant.featured_media_id) detailVariant.featured_media = { id: variant.featured_media_id };
        this.productSection.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: { variant: detailVariant }
        }));
      }

      this.recalc();

      // Pricing tabs are live: recompute garment + decoration tables for the
      // newly selected variant so prices update on colour/variant change.
      if (variant && typeof variant.price === 'number') {
        this.basePrice = variant.price;
      }
      const keepTab = this.activeTab;
      if (this.pricingRows) this.renderPricing('garment');
      this.activeTab = keepTab;
      this.renderDecoration();
    }

    /* ------------------------------ totals ---------------------------------- */
    getItems() {
      const items = [];
      this.grid.querySelectorAll('[data-bsg-qty]').forEach((input) => {
        const qty = parseInt(input.value, 10) || 0;
        const id = input.dataset.variantId;
        const price = parseInt(input.dataset.price, 10) || 0;
        if (qty > 0 && id) items.push({ id: Number(id), quantity: qty, price: price });
      });
      return items;
    }

    // Find the volume-discount % for a given total quantity, using the garment tiers.
    discountForQty(qty) {
      const tiers = (this.pricing && this.pricing.tiers) || [];
      for (let i = 0; i < tiers.length; i++) {
        const t = tiers[i];
        const min = t.min || 0;
        const max = (t.max === undefined || t.max === null) ? Infinity : t.max;
        if (qty >= min && qty <= max) return { pct: t.discount || 0, min: min, max: max };
      }
      return { pct: 0, min: 0, max: Infinity };
    }

    recalc() {
      const items = this.getItems();
      const totalQty = items.reduce((s, i) => s + i.quantity, 0);
      if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;
      const has = totalQty > 0;
      this.addBtn.disabled = !has;
      this.addBtn.setAttribute('aria-disabled', has ? 'false' : 'true');
      if (this.hint) this.hint.hidden = has;

      // Tier for the current total quantity (this page's qty; cart combines at checkout).
      const d = (this.pricing && this.pricing.tiers) ? this.discountForQty(totalQty) : { pct: 0 };

      // ---- Per-row price: update each size row's displayed price in realtime ----
      // Each row's ORIGINAL price is read from the qty input's data-price (set server-side,
      // never overwritten), so we always discount from the true base, not a discounted value.
      this.grid.querySelectorAll('[data-bsg-row]').forEach((row) => {
        const input = row.querySelector('[data-bsg-qty]');
        const priceEl = row.querySelector('[data-bsg-price]');
        if (!input || !priceEl) return;
        const base = parseInt(input.dataset.price, 10) || 0;
        if (!base) return;
        if (d.pct > 0) {
          const discounted = Math.round(base * (1 - d.pct / 100));
          priceEl.innerHTML =
            '<span class="bsg__price-was">' + this.formatMoney(base) + '</span> ' +
            '<span class="bsg__price-now">' + this.formatMoney(discounted) + '</span>';
          priceEl.classList.add('is-discounted');
        } else {
          priceEl.textContent = this.formatMoney(base);
          priceEl.classList.remove('is-discounted');
        }
      });

      // Per-unit discounted price display (summary). Uses this page's qty;
      // true combined-cart discount resolves at checkout via Shopify automatic discount.
      if (this.unitRow && this.pricing && this.pricing.tiers) {
        if (has && items.length) {
          // representative unit price = the price of the first selected size
          // (all sizes of a product usually share a price; if they differ we use the lowest)
          const unitPrice = items.reduce((min, i) => (i.price && i.price < min ? i.price : min), items[0].price || 0);
          const discounted = Math.round(unitPrice * (1 - d.pct / 100));
          this.unitRow.hidden = false;
          if (this.unitPriceEl) {
            if (d.pct > 0) {
              this.unitPriceEl.innerHTML =
                '<span class="bsg__price-was">' + this.formatMoney(unitPrice) + '</span> ' +
                '<span class="bsg__price-now">' + this.formatMoney(discounted) + '</span>';
            } else {
              this.unitPriceEl.textContent = this.formatMoney(unitPrice);
            }
          }

          // Running order TOTAL: sum each size's (qty × its own discounted price).
          // Handles mixed prices (e.g. 3XL costs more) correctly.
          let totalCents = 0;
          let baseTotalCents = 0;
          items.forEach((i) => {
            const lineBase = (i.price || 0) * i.quantity;
            baseTotalCents += lineBase;
            totalCents += Math.round((i.price || 0) * (1 - d.pct / 100)) * i.quantity;
          });
          if (this.totalRow && this.totalPriceEl) {
            this.totalRow.hidden = false;
            if (d.pct > 0) {
              this.totalPriceEl.innerHTML =
                '<span class="bsg__price-was">' + this.formatMoney(baseTotalCents) + '</span> ' +
                '<span class="bsg__price-now">' + this.formatMoney(totalCents) + '</span>';
            } else {
              this.totalPriceEl.textContent = this.formatMoney(totalCents);
            }
          }

          // ---- Decoration (embroidery / print) cost ----
          const deco = this.decorationCost(totalQty); // pounds
          const decoCents = Math.round(deco.total * 100);
          if (this.decoRow && this.decoPriceEl) {
            if (decoCents > 0) {
              this.decoRow.hidden = false;
              const posCount = deco.lines.length;
              if (this.decoLabelEl) {
                this.decoLabelEl.textContent = 'Decoration (' + posCount + ' position' + (posCount === 1 ? '' : 's') + '):';
              }
              this.decoPriceEl.textContent = this.formatMoney(decoCents);
            } else {
              this.decoRow.hidden = true;
            }
          }
          if (this.grandRow && this.grandPriceEl) {
            if (decoCents > 0) {
              this.grandRow.hidden = false;
              this.grandPriceEl.textContent = this.formatMoney(totalCents + decoCents);
            } else {
              this.grandRow.hidden = true;
            }
          }

          if (this.discountNoteEl) {
            if (d.pct > 0) {
              this.discountNoteEl.hidden = false;
              this.discountNoteEl.textContent =
                d.pct + '% volume discount applied at this quantity. Discounts combine across all garments in your cart at checkout.';
            } else {
              this.discountNoteEl.hidden = false;
              this.discountNoteEl.textContent =
                'Add more to unlock volume discounts. Discounts combine across all garments in your cart at checkout.';
            }
          }
        } else {
          this.unitRow.hidden = true;
          if (this.totalRow) this.totalRow.hidden = true;
          if (this.decoRow) this.decoRow.hidden = true;
          if (this.grandRow) this.grandRow.hidden = true;
          if (this.discountNoteEl) this.discountNoteEl.hidden = true;
        }
      }
    }

    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.hidden = !msg;
      this.errorBox.textContent = msg || '';
    }

    /* ------------------------------ add to cart ----------------------------- */
    // Build a penny-product line item for the decoration cost, if configured.
    // Returns null when no fee variant is set or there is nothing to charge.
    decorationFeeLineItem(properties) {
      if (!this.decoFeeVariantId) return null;
      const totalQty = this.getItems().reduce((s, i) => s + i.quantity, 0);
      const deco = this.decorationCost(totalQty);
      const cents = Math.round(deco.total * 100); // penny units
      if (cents <= 0) return null;
      // The fee line carries ONLY its own clean summary — not the full garment
      // logo spec (that already lives on the garment lines).
      const feeProps = {};
      if (this.productTitle) feeProps['For'] = this.productTitle;
      feeProps['Decoration'] = deco.lines.map((l) => `${l.pos}: ${l.application}`).join(', ');
      feeProps['Per item'] = this.formatMoney(Math.round(deco.perUnit * 100));
      feeProps['Items'] = String(totalQty);
      if (deco.setup > 0) feeProps['Setup'] = this.formatMoney(Math.round(deco.setup * 100));
      feeProps['_Decoration charge'] = this.formatMoney(cents);
      return { id: Number(this.decoFeeVariantId), quantity: cents, properties: feeProps };
    }

    async submitCart(properties) {
      const baseItems = this.getItems();
      if (!baseItems.length) return;
      const items = baseItems.map((it) => {
        const obj = { id: it.id, quantity: it.quantity };
        if (properties && Object.keys(properties).length) obj.properties = properties;
        return obj;
      });

      // Append this order's decoration fee as its OWN line. Multiple products each
      // keep their own fee line so charges ADD UP (nothing is lost or overwritten).
      const feeLine = this.decorationFeeLineItem(properties);
      if (feeLine) items.push(feeLine);

      const T = (typeof window !== 'undefined' && window.theme) ? window.theme : {};
      const routes = T.routes || {};
      const settings = T.settings || {};
      const cartRoute = routes.cartAdd || '/cart/add.js';
      const cartUrl = routes.cart || '/cart';

      this.showError(false);
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;

      let sections = 'cart-icon-bubble';
      let drawerSectionId = null;
      if (this.cartDrawer) {
        const sec = this.cartDrawer.closest('.shopify-section');
        if (sec) { drawerSectionId = sec.id.replace('shopify-section-', ''); sections += ',' + drawerSectionId; }
      }

      try {
        const res = await fetch(cartRoute, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) {
          const msg = data.description || data.message || 'Could not add to cart.';
          this.showError(msg); throw new Error(msg);
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        const afterAtc = settings.afterAtc || 'drawer';
        if (afterAtc === 'page') {
          setTimeout(() => { window.location.href = cartUrl; }, 300);
        } else if (this.cartDrawer && data.sections && drawerSectionId && data.sections[drawerSectionId]) {
          this.cartDrawer.renderContents({ sections: data.sections }, afterAtc === 'drawer');
        } else if (this.cartDrawer) {
          if (typeof this.cartDrawer.refresh === 'function') this.cartDrawer.refresh(true);
          if (typeof this.cartDrawer.open === 'function') this.cartDrawer.open();
        } else if (window.location.pathname === cartUrl) {
          window.location.reload();
        }

        document.documentElement.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { items } }));

        this.grid.querySelectorAll('[data-bsg-qty]').forEach((i) => { i.value = 0; });
        this.recalc();
        this.addBtn.classList.remove('is-loading');
        this.addBtn.classList.add('is-success');
        setTimeout(() => this.addBtn.classList.remove('is-success'), 1400);
      } catch (err) {
        this.addBtn.classList.remove('is-loading');
        document.documentElement.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: String(err && err.message ? err.message : err) } }));
      } finally {
        this.recalc();
      }
    }

    // Backwards-compatible alias
    addToCart() { return this.submitCart({}); }
  }

  const boot = (scope) => {
    (scope || document).querySelectorAll('[data-bsg]').forEach((el) => {
      if (el.__bsgMounted) return;
      el.__bsgMounted = true;
      try { el.__bsg = new BulkSizeGrid(el); } catch (e) { /* never break the page */ console && console.warn && console.warn('[bsg]', e); }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
